import { Component, OnDestroy, ViewChild, ViewContainerRef } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import { Observable, ReplaySubject, Subject } from 'rxjs';
import { filter, take, takeUntil, tap } from 'rxjs/operators';

import { environment } from '@env/environment';

import { PopupBaseComponent } from './popups/popup-base/popup-base.component';
import { PopupFactoryInterface, PopupFactoryService } from './popups/popup-factory.service';
import { PopupBaseV2Component } from './popups/v2/popup-base-v2/popup-base-v2.component';
import { ConnectionService } from './shared/services/connection.service';
import { NavigationHistoryService } from './shared/services/navigation-history.service';
import { VersionCheckerService } from './version-checker.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnDestroy {
  @ViewChild('popupPortal', { read: ViewContainerRef, static: true }) popupPortal: ViewContainerRef;
  get networkStatus$(): Observable<boolean> {
    return this.connectionService.networkStatus$;
  }
  title: string = 'acc';
  isSuppressScrollY: boolean;
  config = {};
  showLoader: boolean = false;
  private destroy$: ReplaySubject<void> = new ReplaySubject<void>(1);

  constructor(
    private popupFactory: PopupFactoryService,
    private titleService: Title,
    private router: Router,
    private versionChecker: VersionCheckerService,
    private connectionService: ConnectionService,
    private navigationHistory: NavigationHistoryService,
  ) {
    this.setTitle();
    this.init();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  closePopupHandler(): void {
    this.popupPortal.clear();
    if (document?.body?.classList) {
      document.body.classList.remove('fixed');
    }
    this.isSuppressScrollY = false;
  }

  private init(): void {
    this.navigationHistory.init();
    this.popupFactory.scrollSuppress.subscribe((value: boolean) => (this.isSuppressScrollY = value));
    this.popupFactory.popup$.subscribe(popup => {
      this.createPopupHandler(popup);
    });
    this.popupFactory.closePopup$.subscribe(() => {
      this.closePopupHandler();
    });

    const unsub: Subject<void> = new Subject();
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
        tap(ev => {
          if (ev instanceof NavigationStart) {
            this.showLoader = true;
          }
          if (ev instanceof NavigationEnd) {
            this.showLoader = false;
            unsub.next();
          }
        }),
        takeUntil(unsub),
      )
      .subscribe();

    if (environment.production) {
      this.versionChecker.startChecking();
    }
    this.connectionService.init();
  }

  private createPopupHandler(value: PopupFactoryInterface): void {
    const popupInstance = value.isV2Popup
      ? this.popupPortal.createComponent(PopupBaseV2Component).instance
      : this.popupPortal.createComponent(PopupBaseComponent).instance;
    const popupIndex = this.popupPortal.length - 1;
    popupInstance.title = value.title;
    popupInstance.hideIcon = value.hideIcon;
    popupInstance.popupComponent = value.popupComponent;
    popupInstance.popupData = value.popupData;
    popupInstance.subscribers = value.subscribers;
    popupInstance.popupBodyClass = value.popupBodyClass;
    popupInstance.hideCloseBtn = value.hideCloseBtn;
    popupInstance.preventBgClick = value.preventBgClick;
    popupInstance.isHeightFull = value.isHeightFull;
    popupInstance.isReadLatterConfirm = value.isReadLatterConfirm;
    popupInstance.uid = value.uid;
    document.body.classList.add('fixed');
    this.isSuppressScrollY = true;
    popupInstance.close.pipe(take(1)).subscribe(data => {
      if (popupIndex >= this.popupPortal.length) {
        console.warn('Out of range of popup stack, cleaning up..');
        this.popupPortal.clear();
      } else {
        this.popupPortal.remove(popupIndex);
      }
      if (document?.body?.classList) {
        document.body.classList.remove('fixed');
      }
      this.isSuppressScrollY = false;
      this.popupFactory.popupOutput$.next(data);
    });
  }

  private setTitle(): void {
    this.titleService.setTitle(environment.title);
  }
}
